import { isEmpty } from 'lodash';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { addHours, isBefore, parseISO } from 'date-fns';
import { contactService } from 'config/ioc';
import { FULFILLED, PENDING, REJECTED } from 'constants/actions';
import routerStore from './RouterStore';
import { handleSentryDataError } from '../utils/errors';

const DEFAULT_ERROR = {
  validationErrors: {},
  baseErrors: {},
};

class ContactStore {
  fetchState;

  updateContactState;

  addContactState;

  scanState;
  newContact;
  _contactId;
  _customerId;
  scannedFields = {};
  _filters = {};
  backFromPath = null;
  scrollIndex = null;
  filterParams = '';
  errors = DEFAULT_ERROR;

  constructor() {
    makeObservable(this, {
      fetchState: observable,
      updateContactState: observable,
      addContactState: observable,
      scanState: observable,
      _contacts: observable.ref,
      newContact: observable.ref,
      _contactId: observable,
      _customerId: observable,
      scannedFields: observable.ref,
      _filters: observable.shallow,
      backFromPath: observable,
      contacts: computed,
      contactList: computed,
      contact: computed,
      getContacts: action,
      saveFilterParams: action,
      setFilter: action.bound,
      clearFilter: action.bound,
      setBackFrom: action.bound,
      clearBackFrom: action.bound,
      fetchContacts: action,
      setContactId: action.bound,
      setCustomerId: action.bound,
      addContact: action.bound,
      updateContact: action,
      analyzeCard: action.bound,
    });

    reaction(
      () => this._contactId,
      () => {
        if (this._contacts.length === 0) {
          this.getContacts(this._customerId);
        }
      },
    );
  }

  _contacts = [];

  get contacts() {
    const filters = Object.entries(this._filters);

    return this._contacts.filter((contact) => {
      return filters.every(([key, value]) => {
        if (value === null || value === undefined) {
          return true;
        }

        if (key === 'query') {
          const matchPhoneNumber = Object.values(contact.phone).some(
            (phone) => {
              return phone.includes(value);
            },
          );
          const { name } = contact;
          const fullName = `${name.last} ${name.first}`.trim();
          const matchName = fullName.includes(value);

          return matchName || matchPhoneNumber;
        }

        if (key === 'customerId' && value) {
          return contact.customer.id === value;
        }

        if (key === 'consignee') {
          return contact.role.consignee === value;
        }

        if (key === 'purchasing') {
          return contact.role.purchasing === value;
        }

        if (key === 'payments') {
          return contact.role.payments === value;
        }

        return true;
      });
    });
  }

  get contactList() {
    return this.contacts.map((contact) => {
      return {
        id: contact.contactId,
        firstName: contact.name.first,
        lastName: contact.name.last,
        phone: contact.phone.first,
        job: contact.job,
        customer: contact.customer,
      };
    });
  }

  get contact() {
    const result = this._contacts.find((contact) => {
      return contact.contactId === this._contactId;
    });

    return result;
  }

  getContacts(customerId) {
    const cachedContacts = JSON.parse(localStorage.getItem('contacts'));
    const now = new Date();

    if (
      !isEmpty(cachedContacts) &&
      isBefore(now, parseISO(cachedContacts?.expiredAt))
    ) {
      this._contacts = cachedContacts.items;
      this.fetchState = FULFILLED;
    } else {
      this.fetchContacts(customerId);
    }
  }

  saveFilterParams(queryParams) {
    this.filterParams = queryParams;
  }

  setFilter(name, value) {
    this._filters[name] = value;
  }

  clearFilter() {
    this._filters = {};
  }

  setBackFrom(path) {
    if (!this.backFromPath) {
      this.backFromPath = path;
    }
  }

  clearBackFrom() {
    this.backFromPath = null;
  }

  async fetchContacts(customerId) {
    this.fetchState = PENDING;

    try {
      const response = await contactService.getContacts(customerId);
      const contacts = response.data.data.items;
      this._contacts = contacts;
      const expiredAt = addHours(new Date(), 4);
      localStorage.setItem(
        'contacts',
        JSON.stringify({ expiredAt, items: contacts }),
      );
      this.fetchState = FULFILLED;
    } catch (e) {
      this.fetchState = REJECTED;
    }
  }

  setContactId(contactId) {
    this._contactId = contactId;
  }

  setCustomerId(customerId) {
    this._customerId = customerId;
  }

  async addContact(data) {
    this.addContactState = PENDING;
    this.errors = { ...DEFAULT_ERROR };
    try {
      const response = await contactService.addContact(data);
      const [newContact] = response.data.data.items;
      this.newContact = newContact;
      this.getContacts(newContact.customer.id);
      this._contacts = [...this._contacts, newContact];
      const expiredAt = addHours(new Date(), 4);
      localStorage.setItem(
        'contacts',
        JSON.stringify({ expiredAt, items: this._contacts }),
      );
      this.addContactState = FULFILLED;
      routerStore.push(
        `/customers/${newContact.customer.id}/contacts/${newContact.contactId}`,
      );
    } catch (e) {
      if (e.errors) {
        this.errors = e.errors;
      } else {
        this.errors = handleSentryDataError(e);
      }
      this.addContactState = REJECTED;
    }
  }

  async updateContact(data) {
    this.updateContactState = PENDING;
    this.errors = { ...DEFAULT_ERROR };
    try {
      const response = await contactService.updateContact(data);
      const [updatedContact] = response.data.data.items;
      this.getContacts(updatedContact.customer.id);
      const newContacts = this._contacts.map((contact) =>
        contact.contactId === updatedContact.contactId
          ? updatedContact
          : contact,
      );
      this._contacts = newContacts;
      const expiredAt = addHours(new Date(), 4);
      localStorage.setItem(
        'contacts',
        JSON.stringify({ expiredAt, items: this._contacts }),
      );
      this.updateContactState = FULFILLED;
    } catch (e) {
      if (e.errors) {
        this.errors = e.errors;
      } else {
        this.errors = handleSentryDataError(e);
      }
      this.updateContactState = REJECTED;
    }
  }

  async analyzeCard(customerId, file) {
    this.scanState = PENDING;

    try {
      const response = await contactService.analyzeCard(customerId, file);
      const [scannedFields] = response.data.data.items;
      this.scannedFields = scannedFields;
      this.scanState = FULFILLED;
    } catch (e) {
      if (!e.errors) {
        this.errors = handleSentryDataError(e);
      }
      this.scanState = REJECTED;
    }
  }
}

const contactStore = new ContactStore();

export default contactStore;
